import { useState } from "react";
import { NAVY } from "../styles/palette";
import { getPrice } from "../lib/pricing";
import { getServiceDayForClient } from "../lib/serviceArea";
import { Icon } from "./Icon";
import { ACTIVE_THEME } from "../themes";
import { ClientsApple } from "./ClientsApple";

export function Clients({ clients, onAdd, onEdit, onToggleActive, onViewProfile, technicians, onBulkAssign, pricing, showToast }) {
  const [search,       setSearch]       = useState("");
  const [clientTab,    setClientTab]    = useState("all");
  const [selected,     setSelected]     = useState(new Set());
  const [bulkTech,     setBulkTech]     = useState("");
  const [assigningBulk,setAssigningBulk]= useState(false);
  const unassignedCount = clients.filter(c=>!c.technician_id&&c.active).length;
  // Subscription-status filter mirrors the status badge logic (deactivated/canceled or
  // !c.active = inactive). Search now combines with ANY tab, so you can e.g. find a
  // paused client by name.
  const filtered = clients.filter(c=>{
    const inactive = c.status==="deactivated" || c.status==="canceled" || c.active===false;
    const matchTab =
      clientTab==="unassigned"      ? (!c.technician_id && c.active) :
      clientTab==="payment_pending" ? (c.status==="payment_pending") :
      clientTab==="residential"     ? (!c.client_type || c.client_type==="residential") :
      clientTab==="commercial"      ? (c.client_type==="commercial") :
      clientTab==="active"          ? (!inactive && (c.status||"active")==="active") :
      clientTab==="paused"          ? (!inactive && c.status==="paused") :
      clientTab==="deactivated"     ? inactive :
      clientTab==="issues"          ? (!inactive && (c.status==="paused" || c.status==="payment_pending")) :
      true; // "all"
    if (!matchTab) return false;
    const q = search.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || (c.zip||"").includes(q) || (c.email||"").toLowerCase().includes(q);
  });

  // Bulk-assign action (shared by both layouts) — identical behavior either way.
  const applyBulk = async () => {
    setAssigningBulk(true);
    await onBulkAssign([...selected], bulkTech==="unassign"?null:bulkTech);
    setSelected(new Set()); setBulkTech(""); setAssigningBulk(false);
  };

  // apple-native template owns its own Clients layout (real redesign). Same state,
  // setters, and handlers — zero functional change. "default" falls through below.
  if (ACTIVE_THEME === "apple-native") {
    return <ClientsApple
      clients={clients} filtered={filtered} technicians={technicians} pricing={pricing}
      search={search} setSearch={setSearch} clientTab={clientTab} setClientTab={setClientTab}
      unassignedCount={unassignedCount} selected={selected} setSelected={setSelected}
      bulkTech={bulkTech} setBulkTech={setBulkTech} assigningBulk={assigningBulk} applyBulk={applyBulk}
      onViewProfile={onViewProfile} onEdit={onEdit} showToast={showToast}
    />;
  }

  return (
    <div>
      {/* Tabs */}
      <div style={{display:"flex",flexWrap:"wrap",gap:0,borderBottom:"2px solid #e3e8ef",marginBottom:16}}>
        {[
          {val:"all",         label:"All Clients",  count:clients.length},
          {val:"active",      label:"Active",       count:clients.filter(c=>c.active!==false&&(c.status||"active")==="active").length},
          {val:"paused",      label:"Paused",       count:clients.filter(c=>c.active!==false&&c.status==="paused").length},
          {val:"deactivated", label:"Inactive",     count:clients.filter(c=>c.status==="deactivated"||c.status==="canceled"||c.active===false).length},
          {val:"residential", label:"Residential",  count:clients.filter(c=>!c.client_type||c.client_type==="residential").length},
          {val:"commercial",  label:"Commercial",   count:clients.filter(c=>c.client_type==="commercial").length},
          {val:"unassigned",      label:"Unassigned",       count:unassignedCount, alert:true},
          {val:"payment_pending", label:"💳 Payment Pending", count:clients.filter(c=>c.status==="payment_pending").length, alert:true},
        ].map(({val,label,count,alert})=>(
          <div key={val} onClick={()=>setClientTab(val)}
            style={{padding:"8px 16px",cursor:"pointer",fontSize:13,fontWeight:clientTab===val?600:500,
              color:clientTab===val?NAVY:"#8993a4",borderBottom:`2px solid ${clientTab===val?NAVY:"transparent"}`,marginBottom:-2,display:"flex",alignItems:"center",gap:6}}>
            {label}
            {count>0&&<span style={{background:alert&&count>0?"#ffebe6":"#f4f5f7",color:alert&&count>0?"#bf2600":"#42526e",fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:10}}>{count}</span>}
          </div>
        ))}
      </div>
      <div className="flex gap-2" style={{marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, email, or zip..." style={{flex:1,minWidth:200}} />
        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#42526e",cursor:"pointer",userSelect:"none"}}>
          <input type="checkbox" checked={selected.size===filtered.length&&filtered.length>0}
            onChange={e=>setSelected(e.target.checked?new Set(filtered.map(c=>c.id)):new Set())}
            style={{accentColor:NAVY}}/>
          Select all
        </label>
      </div>
      {selected.size>0&&(
        <div style={{background:"#deebff",border:"1px solid #b3d4ff",borderRadius:8,padding:"10px 14px",marginBottom:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:13,fontWeight:600,color:NAVY}}>{selected.size} client{selected.size!==1?"s":""} selected</span>
          <select value={bulkTech} onChange={e=>setBulkTech(e.target.value)} style={{fontSize:12,minWidth:160}}>
            <option value="">— Assign to tech —</option>
            {(technicians||[]).filter(t=>t.active).map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            <option value="unassign">Unassign</option>
          </select>
          <button className="btn btn-gold btn-sm" disabled={!bulkTech||assigningBulk}
            onClick={async()=>{
              setAssigningBulk(true);
              await onBulkAssign([...selected], bulkTech==="unassign"?null:bulkTech);
              setSelected(new Set()); setBulkTech(""); setAssigningBulk(false);
            }}>
            {assigningBulk?"Assigning...":"✓ Apply"}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={()=>setSelected(new Set())}>Cancel</button>
        </div>
      )}
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th style={{width:32}}></th><th>Client</th><th>Dogs</th><th>Frequency</th><th>Monthly</th><th>Technician</th><th>Card</th><th>Status</th></tr>
            </thead>
            <tbody>
              {filtered.map(c=>(
                <tr key={c.id} style={{cursor:"pointer"}} onClick={()=>onViewProfile(c)}>
                  <td style={{width:32}} onClick={e=>e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(c.id)}
                      onChange={e=>setSelected(s=>{ const n=new Set(s); e.target.checked?n.add(c.id):n.delete(c.id); return n; })}
                      style={{accentColor:NAVY}}/>
                  </td>
                  <td>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{fontWeight:600,color:NAVY}}>{c.name}</div>
                      {c.client_type==="commercial"&&<span style={{fontSize:9,background:"#deebff",color:"#0747a6",padding:"1px 5px",borderRadius:4,fontWeight:700}}>COMMERCIAL</span>}
                      {!getServiceDayForClient(c)&&<span style={{fontSize:9,background:"#ffebe6",color:"#bf2600",padding:"1px 5px",borderRadius:4,fontWeight:700}}>NO SERVICE DAY</span>}
                    </div>
                    <div style={{fontSize:11,color:"#8993a4",marginTop:2}}>{c.email}</div>
                    <div style={{fontSize:11,color:"#8993a4"}}>{getServiceDayForClient(c) ? getServiceDayForClient(c).charAt(0).toUpperCase()+getServiceDayForClient(c).slice(1)+"s" : "No service day"} · {c.zip} · {c.phone}</div>
                  </td>

                  <td>{c.dogs} 🐕</td>
                  <td>
                    <div style={{textTransform:"capitalize",fontSize:12}}>{c.frequency}</div>
                    {c.pending_frequency&&<div style={{fontSize:10,color:"#974f0c",background:"#fffae6",padding:"1px 5px",borderRadius:3,marginTop:2}}>→ {c.pending_frequency} on {c.pending_change_date}</div>}
                  </td>
                  <td style={{fontWeight:700,color:NAVY}}>${getPrice(c.pending_frequency||c.frequency, c.pending_dogs||c.dogs, pricing).toFixed(2)}</td>
                  <td>
                    {c.technician_name
                      ? <span style={{fontSize:12,fontWeight:600,color:NAVY}}>👤 {c.technician_name}</span>
                      : <span style={{fontSize:11,color:"#bf2600",background:"#ffebe6",padding:"2px 6px",borderRadius:4}}>Unassigned</span>
                    }
                  </td>
                  <td>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}>
                      {c.stripe_pm_last4
                        ? <span style={{fontSize:12,color:"#166534",background:"#dcfce7",padding:"2px 8px",borderRadius:4}}>🔒 ···· {c.stripe_pm_last4}</span>
                        : <span style={{fontSize:12,color:"#991b1b",background:"#fee2e2",padding:"2px 8px",borderRadius:4}}>⚠️ No card</span>
                      }
                      {c.needs_proration&&<span style={{fontSize:11,color:"#854d0e",background:"#fef9c3",padding:"2px 8px",borderRadius:4}}>💰 First payment due</span>}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${(c.status||"active")==="active"?"badge-green":(c.status)==="paused"?"badge-gold":"badge-gray"}`} style={{textTransform:"capitalize"}}>
                      {c.status==="paused"?"⏸ Paused":c.status==="deactivated"?"Deactivated":"Active"}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>onViewProfile(c)}>Profile</button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>onEdit(c)}><Icon n="edit" size={12}/></button>

                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length===0 && <tr><td colSpan={9}><div className="empty"><p>No clients found</p></div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
